<template>
  <div class="video-link-card-parts" @click="emitClick">
    <div
      class="video-link-card-parts__link"
      :class="{
        'video-link-card-parts__link--disabled':
          videoLink.membership === 'paid' && membership === 'free',
      }"
    >
      <figure><img class="video-link-card-parts__img" :src="thumbnailPath" alt="" /></figure>
      <span
        class="video-link-card-parts__tag"
        v-if="videoLink.membership === 'paid'"
        v-text="$tc('HomePage.limitedVideoMembershipTag')"
      />
      <p class="video-link-card-parts__title">
        {{ videoLink.title }}
      </p>
      <div class="video-link-card-parts__meta">
        <span class="video-link-card-parts__date">{{ videoLink.date }}</span>
      </div>

      <span
        class="video-link-card-parts__premium"
        v-if="premiumLabelEnabled"
        v-text="$tc('HomePage.limitedVideoPremiumTag')"
      />
      <i
        class="video-link-card-parts__lock-icon"
        v-else-if="videoLink.membership === 'paid' && membership === 'free'"
      >
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M10.6667 6.5013V5.16797C10.6667 3.69521 9.47276 2.5013 8 2.5013C6.52724 2.5013 5.33333 3.69521 5.33333 5.16797V6.5013H10.6667ZM8 1.16797C5.79086 1.16797 4 2.95883 4 5.16797V7.83463H12V5.16797C12 2.95883 10.2091 1.16797 8 1.16797Z"
            fill="#ABABAB"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.83398 6.4987C2.00556 6.4987 1.33398 7.17027 1.33398 7.9987L1.33398 14.332C1.33398 15.1605 2.00556 15.832 2.83398 15.832L13.1673 15.832C13.9957 15.832 14.6673 15.1605 14.6673 14.332L14.6673 7.9987C14.6673 7.17027 13.9957 6.4987 13.1673 6.4987L2.83398 6.4987ZM8.50065 13.4987V11.4018C8.98929 11.204 9.33398 10.7249 9.33398 10.1654C9.33398 9.42899 8.73703 8.83203 8.00065 8.83203C7.26427 8.83203 6.66732 9.42899 6.66732 10.1654C6.66732 10.7249 7.01201 11.204 7.50065 11.4018L7.50065 13.4987L8.50065 13.4987Z"
            fill="#ABABAB"
          />
        </svg>
      </i>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'

/**
 * 無料会員、有料会員かを示す
 */
export type VideoLinkCardMembershipData = 'free' | 'paid'

/**
 * HOMEの動画カードのデータモデル
 */
export type VideoLinkCardData = {
  img: string
  title: string
  link: string
  date: string
  membership: VideoLinkCardMembershipData
}

/**
 * HOME Youtubeリストセクション
 */
export default defineComponent({
  name: 'VideoLinkCardParts',
  props: {
    videoLink: {
      type: Object as PropType<VideoLinkCardData>,
      required: true,
    },
    membership: {
      type: String as PropType<VideoLinkCardMembershipData>,
      required: true,
    },
  },
  components: {},
  setup(props) {
    // サムネイル画像のURLをCloudFrontの署名URLに変換する
    const thumbnailPath = CloudFrontUtil.getSignedUrl(props.videoLink.img)

    // 現状、PREMIUM の表記は無し
    // const premiumLabelEnabled = props.videoLink.membership === 'paid' && props.membership === 'paid'
    const premiumLabelEnabled = false
    return {
      thumbnailPath,
      premiumLabelEnabled,
    }
  },
  methods: {
    /**
     * モーダル表示 通知
     */
    emitClick() {
      this.$emit('click')
    },
  },
})
</script>

<style lang="scss" scoped>
.video-link-card-parts {
  width: 220px;
  min-width: 220px;
  cursor: pointer;

  &__link {
    position: relative;
    display: block;
    width: 100%;
    background: $white;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    overflow: hidden;

    &--disabled {
      pointer-events: none;
    }
  }

  &__img {
    border-radius: 12px 12px 0 0;
    margin-bottom: 10px;
    height: 123px;
    width: 100%;
    object-fit: cover;
  }

  &__tag {
    position: absolute;
    top: 8px;
    left: 9px;
    display: inline-block;
    padding: 4px 6px;
    background: rgba(0, 219, 166, 0.7);
    border-radius: 2px;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 10px;
    line-height: 1.4;
    letter-spacing: 0.04em;
    color: $white;
  }

  &__title {
    display: -webkit-box;
    overflow: hidden;
    height: 3.08em;
    width: 100%;
    padding: 0 12px;
    @include fontSetTextMedium();

    font-family: $fontSignup;
    letter-spacing: 0.04em;
    font-feature-settings: 'palt' on;
    color: $black;

    /* autoprefixer: off */
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    /*
      以下のエラーがでるため、ブロックを分けて記載する。
      `Second Autoprefixer control comment was ignored. Autoprefixer applies control comment to whole block, not to next rules.`
    */
  }

  /* autoprefixer: on */

  &__meta {
    padding: 7px 12px 10px;
    font-size: 10px;
    line-height: 1.2;
  }

  &__date {
    font-weight: 600;
    letter-spacing: 0.04em;
    color: $black50;
  }

  &__premium {
    position: absolute;
    right: 11px;
    bottom: 6px;
    font-family: $fontHeading;
    font-weight: 600;
    font-size: 11px;
    line-height: 1.64;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $gray10;
  }

  &__lock-icon {
    position: absolute;
    right: 12px;
    bottom: 8px;
  }
}
</style>
